"use client";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiCopy, handleCopy } from "../common/commonFunction/handleCopy";
import styles from "./BinaryText.module.css";

function BinaryText({ Data }) {
  const [text, setText] = useState("");
  const [text2, setText2] = useState(null);
  const [copy, setCopy] = useState(false);
  const [errors, setErrors] = useState("");

  const handleOnErase = () => {
    setText("");
    setText2("");
    setCopy(false);
  };

  const handleOnChange = (event) => {
    setErrors("");
    setText(event.target.value);
    setText2("");
  };

  useEffect(() => {
    setText2("");
    setText("");
    setCopy(false);
    setErrors("");
  }, [Data]);

  const texttobinary = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter text.");
    setCopy(false);
    let data = "";
    for (let i = 0; i < text.length; i++) {
      data += text[i].charCodeAt(0).toString(2) + " ";
    }
    setText2(data);
  };

  const binarytotext = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a binary number.");
      return;
    }
    try {
      setCopy(false);
      const binaryArray = text.trim().split(" ");
      const charArray = binaryArray.map((binary) => {
        const decimal = parseInt(binary, 2);
        if (isNaN(decimal)) throw new Error(`Invalid binary number: ${binary}`);
        return String.fromCharCode(decimal);
      });
      const newText = charArray.join("");
      setText2(newText);
      setErrors("");
    } catch (error: any) {
      setErrors(error.message || "* An error occurred.");
    }
  };

  const asciitoBinary = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter text.");
      return;
    }

    setCopy(false);

    try {
      const binaryArray = text.split("").map((char) => {
        const binary = char.charCodeAt(0).toString(2);
        return binary.padStart(8, "0");
      });

      const result = binaryArray.join(" ");
      setText2(result);
      setErrors("");
    } catch (error: any) {
      setErrors(error.message || "* An error occurred.");
    }
  };

  const binarytoAscii = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a valid binary string.");
      return;
    }
    setCopy(false);
    try {
      const binaryChunks = text.match(/.{1,8}/g);
      const result = binaryChunks
        ?.map((chunk) => String.fromCharCode(parseInt(chunk, 2)))
        .join("");
      setText2(result || "");
      setErrors("");
    } catch (error: any) {
      setErrors(error.message || "* An error occurred.");
    }
  };

  const texttoascii = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter text.");
    setCopy(false);
    let data = [];
    data = text.split("");
    let array = [];
    for (let index = 0; index < data.length; index++) {
      array.push(data[index].charCodeAt(data[index]));
    }

    setText2(array.join(" "));
  };

  const ConvertBase = (num: string) => ({
    from: (baseFrom: number) => ({
      to: (baseTo: number) => parseInt(num, baseFrom).toString(baseTo),
    }),
  });

  const bin2hex = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter number.");
    setText2(ConvertBase(text.split(" ").join("")).from(2).to(16));
  };
  const bin2dec = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a binary number.");
      return;
    }
    try {
      const sanitizedBinary = text.split(" ").join("");
      if (!/^[01]+$/.test(sanitizedBinary)) {
        setErrors("* Please enter a valid binary number (only 0s and 1s).");
        return;
      }
      const decimalResult = parseInt(sanitizedBinary, 2);
      setText2(decimalResult.toString());
      setErrors("");
    } catch (error) {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };

  const dec2bin = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a number.");
      return;
    }

    try {
      const decimal = parseInt(text.trim(), 10);
      if (isNaN(decimal)) {
        setErrors("* Please enter a valid decimal number.");
        return;
      }
      setText2(decimal.toString(2));
      setErrors("");
    } catch {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };

  const dec2hex = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a number.");
      return;
    }

    try {
      const decimal = parseInt(text.trim(), 10);
      if (isNaN(decimal)) {
        setErrors("* Please enter a valid decimal number.");
        return;
      }
      setText2(decimal.toString(16).toUpperCase());
      setErrors("");
    } catch {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };

  const hex2dec = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a number.");
      return;
    }

    try {
      const hexadecimal = text.trim();
      if (!/^[0-9a-fA-F]+$/.test(hexadecimal)) {
        setErrors("* Please enter a valid hexadecimal number.");
        return;
      }
      setText2(parseInt(hexadecimal, 16).toString());
      setErrors("");
    } catch {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };

  const hex2bin = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a hexadecimal value.");
      return;
    }
    try {
      setCopy(false);
      const hexadecimal = text.trim().replace(/\s+/g, "");
      if (!/^[0-9a-fA-F]+$/.test(hexadecimal)) {
        setErrors("* Please enter a valid hexadecimal number (0-9, A-F).");
        return;
      }
      const decimal = parseInt(hexadecimal, 16);
      if (!Number.isFinite(decimal)) {
        setErrors("* Hexadecimal value is too large.");
        return;
      }
      setText2(decimal.toString(2));
      setErrors("");
    } catch {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };

  const hex2txt = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter number.");
    let hex = text.toString().split("").join("");
    let str = "";
    for (let n = 0; n < hex.length; n += 2) {
      str += String.fromCharCode(parseInt(hex.substr(n, 2), 16));
    }
    setText2(str);
  };

  const txt2hex = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter text.");
    const hex = require("string-hex");
    setText2(hex(text).split("").join(""));
  };

  const txt2dec = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter text.");
    setCopy(false);
    let data = [];
    data = text.split("");
    let array = [];
    for (let index = 0; index < data.length; index++) {
      array.push(data[index].charCodeAt(data[index]));
    }
    setText2(array.join(" "));
  };

  const dec2txt = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a valid input.");
      return;
    }
    try {
      const bytes = text.trim().replace(/\s+/g, " ").split(" ");

      const invalidInput = bytes.some(
        (byte) => isNaN(Number(byte)) || Number(byte) < 0 || Number(byte) > 255
      );
      if (invalidInput) {
        setErrors("* Please enter valid decimal numbers (0-255).");
        return;
      }
      const result = bytes
        .map((byte) => String.fromCharCode(Number(byte)))
        .join("");

      setText2(result);
      setErrors("");
    } catch {
      setErrors("* An unexpected error occurred during the conversion.");
    }
  };
  const octal2binary = () => {
    if (!text || text.trim() === "")
      return setErrors("* Please enter octal value.");
    setCopy(false);
    let i = 0;
    let binary = "";
    while (i < text.length) {
      let c = text[i];
      switch (c) {
        case "0":
          binary += "000";
          break;
        case "1":
          binary += "001";
          break;
        case "2":
          binary += "010";
          break;
        case "3":
          binary += "011";
          break;
        case "4":
          binary += "100";
          break;
        case "5":
          binary += "101";
          break;
        case "6":
          binary += "110";
          break;
        case "7":
          binary += "111";
          break;
      }
      i++;
    }
    setText2(binary);
    return binary;
  };

  const octal2decimal = () => {
    if (!text || text.trim() === "") {
      return setErrors("* Please enter an octal value.");
    }
    setCopy(false);
    let num = text.replace(/\s+/g, "");
    let dec_value = 0;
    let base = 1;
    let temp = parseInt(num, 8);
    if (isNaN(temp)) {
      return setErrors("* Please enter a valid octal value.");
    }

    while (temp > 0) {
      let last_digit = temp % 10;
      temp = Math.floor(temp / 10);
      dec_value += last_digit * base;
      base = base * 8;
    }
    setText2(dec_value);
    return dec_value;
  };

  const octal2hex = () => {
    if (!text || text.trim() === "") {
      return setErrors("* Please enter octal value.");
    }
    setCopy(false);
    const cleanedInput = text.replace(/\s+/g, "");
    if (!/^[0-7]+$/.test(cleanedInput)) {
      return setErrors("* Please enter a valid octal value.");
    }
    const hexValue = ConvertBase(parseInt(cleanedInput, 8).toString())
      .from(10)
      .to(16);
    setText2(hexValue);
  };

  const octal2text = () => {
    if (!text || text.trim() === "") {
      return setErrors("* Please enter octal value.");
    }
    setCopy(false);
    let octal = text.replace(/\s+/g, "");
    const isValidOctal = /^[0-7]+$/.test(octal);

    if (!isValidOctal) {
      return setErrors("* Please enter a valid octal value.");
    }
    let result = "";
    for (let i = 0; i < octal.length; i += 3) {
      const octalChunk = octal.substr(i, 3);
      result += String.fromCharCode(parseInt(octalChunk, 8));
    }

    setText2(result);
  };

  const text2octal = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter text.");
    setCopy(false);
    let data = "";
    for (let i = 0; i < text.length; i++) {
      data += text[i].charCodeAt(0).toString(8) + " ";
    }
    setText2(data);
  };

  const convertToOctal = (base: number) => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter a valid number.");
      return;
    }

    try {
      setCopy(false);
      const cleanedInput = text.replace(/\s+/g, "");
      const isValid =
        base === 16
          ? /^[0-9A-Fa-f]+$/.test(cleanedInput)
          : /^[0-9]+$/.test(cleanedInput);
      if (!isValid) {
        setErrors("* Please enter a valid number for the specified base.");
        return;
      }
      const decimalValue = parseInt(cleanedInput, base);
      if (isNaN(decimalValue)) {
        setErrors("* Conversion failed. Please check your input.");
        return;
      }

      const octalResult = decimalValue.toString(8);
      setText2(octalResult);
      setErrors("");
    } catch (error) {
      setErrors(
        "* An error occurred during the conversion. Please check your input."
      );
    }
  };

  const binary2octal = () => convertToOctal(2);
  const decimal2octal = () => convertToOctal(10);
  const hex2octal = () => convertToOctal(16);

  const json2txt = () => {
    if (!text || text.trim() === "") return setErrors("* Please enter json.");
    let convert = require("json-to-plain-text");
    try {
      let response = convert.toPlainText(JSON.parse(text));
      setText2(response);
    } catch (error) {
      toast.error("It's Not Valid JSON, Enter Valid Base Value");
    }
  };

  const ascii2txt = () => {
    if (!text || text.trim() === "") {
      setErrors("* Please enter ASCII codes separated by spaces.");
      return;
    }
    try {
      setCopy(false);
      const asciiArray = text.trim().split(" ");
      const isValid = asciiArray.every(
        (elem) => /^[0-9]+$/.test(elem) && +elem <= 255
      );
      if (!isValid) {
        setErrors(
          "* Please ensure all input values are valid ASCII codes (0-255)."
        );
        return;
      }
      const convertedText = asciiArray
        .map((asciiCode) => String.fromCharCode(parseInt(asciiCode, 10)))
        .join("");
      setText2(convertedText);
      setErrors("");
    } catch (error) {
      setErrors(
        "* An error occurred during conversion. Please check your input."
      );
    }
  };

  const CustomPlaceholder = () => {
    switch (Data.navigate) {
      case "/text-to-binary":
        return texttobinary;

      case "/binary-to-text":
        return binarytotext;

      case "/binary-to-ascii":
        return binarytoAscii;

      case "/ascii-to-binary":
        return asciitoBinary;

      case "/text-to-ascii":
        return texttoascii;

      case "/binary-to-hex":
        return bin2hex;

      case "/binary-to-decimal":
        return bin2dec;

      case "/decimal-to-binary":
        return dec2bin;

      case "/decimal-to-hex":
        return dec2hex;

      case "/hex-to-decimal":
        return hex2dec;

      case "/hex-to-binary":
        return hex2bin;

      case "/text-to-hex":
        return txt2hex;

      case "/hex-to-text":
        return hex2txt;

      case "/text-to-decimal":
        return txt2dec;

      case "/octal-to-binary":
        return octal2binary;

      case "/octal-to-decimal":
        return octal2decimal;

      case "/octal-to-hex":
        return octal2hex;

      case "/octal-to-text":
        return octal2text;

      case "/text-to-octal":
        return text2octal;

      case "/binary-to-octal":
        return binary2octal;

      case "/decimal-to-octal":
        return decimal2octal;

      case "/hex-to-octal":
        return hex2octal;

      case "/decimal-to-text":
        return dec2txt;

      case "/ascii-to-text":
        return ascii2txt;

      case "/json-to-text":
        return json2txt;

      default:
        return null;
    }
  };

  return (
    <>
      <div>
        <div className="children-box-alignment">
          <div className="main-globally-counter-box">
            {text?.length > 0 && (
              <div className="color-picker-icon">
                <i
                  className="fa-solid fa-eraser"
                  title="Clear"
                  onClick={handleOnErase}
                ></i>
              </div>
            )}
            <div className="counter-text-area">
              <textarea
                className={errors && "error-input"}
                placeholder={Data.placeholder}
                onChange={handleOnChange}
                value={text}
              />
              <span>{errors}</span>
            </div>
          </div>
          <div className="button lowercase-center-alignment">
            <button onClick={CustomPlaceholder()}>{Data.Name}</button>
          </div>
          {text2 && (
            <div className="main-globally-counter-box">
              <div className="copy-textarea-icon-alignment">
                <div onClick={() => handleCopy(text2, setCopy)} role="button">
                  <FiCopy />
                </div>
                {copy && (
                  <div>
                    <span>Copied</span>
                  </div>
                )}
              </div>
              <div className="counter-text-area">
                <textarea value={text2} readOnly />
              </div>
            </div>
          )}
        </div>
      </div>
      {Data?.description && (
        <div className={styles.description}>
          <p className={styles.descriptionParagraph}>{Data.description}</p>
        </div>
      )}
    </>
  );
}

export default BinaryText;
